import { Col, Table, TableActionBarRow } from "melco-ui";
import { useTranslation } from "react-i18next";
import { useState } from "react";
import { useRolesContext } from "../../hooks/roles/useRolesContext";
import { Search } from "../search/Search";
import { PublishedState } from "../state/PublishedState";
import { TableCellLink } from "../table/TableCellLink";
import { ControlcenterMelcoModelsSupplierProduct } from "melco-shared-logic";
import { useManageSupplierProducts } from "../../hooks/supplierproducts/useManageSupplierProducts";
import { FormattedDate } from "../table/FormattedDate";
import { SupplierProductEditDrawer } from "./drawer/SupplierProductEditDrawer";
import { SupplierProductAddDrawer } from "./drawer/SupplierProductAddDrawer";

type ManageSupplierProductsListPropTypes = {
  isSupplierProductAddDrawerOpen: boolean;
  setSupplierProductAddDrawerOpenFunction: (v: boolean) => void;
  supplierIdToUpdate: string;
};

export const ManageSupplierProductsList: React.FC<
  ManageSupplierProductsListPropTypes
> = ({
  supplierIdToUpdate,
  isSupplierProductAddDrawerOpen,
  setSupplierProductAddDrawerOpenFunction,
}) => {
  const {
    isLoading,
    isFetching,
    data: supplierProductsData,
    isPreviousData,
    errorComponent,
    onTableConfigChange,
    pagination,
    refetch,
    searchQueryChange,
  } = useManageSupplierProducts();

  const { t } = useTranslation();
  const { roles } = useRolesContext();

  const [
    isSupplierProductEditDrawerOpenValue,
    setIsSupplierProductEditDrawerOpenValue,
  ] = useState<boolean>(isSupplierProductAddDrawerOpen);
  const [supplierProductIdToUpdate, setSupplierProductIdToUpdateValue] =
    useState<string>();

  const handleEditClick = (supplierProductId: string) => {
    setIsSupplierProductEditDrawerOpenValue(true);
    setSupplierProductIdToUpdateValue(supplierProductId);
  };

  const columns = [
    {
      title: t("supplierproducts.table.columns.id"),
      key: "id",
      sorter: true,
      render: (rowObj: any) => {
        return roles.SYSTEM_SUPPLIERPRODUCTS_EDIT ? (
          <TableCellLink
            onClick={() => {
              handleEditClick(rowObj.id);
            }}
            to={""}
          >
            {rowObj.id}
          </TableCellLink>
        ) : (
          rowObj.id
        );
      },
    },

    {
      title: t("supplierproducts.table.columns.name"),
      key: "name",
      dataIndex: "name",
      sorter: true,
    },
    {
      title: t("supplierproducts.table.columns.supplier"),
      key: "supplier_name",
      dataIndex: "supplier_name",
      sorter: true,
    },
    {
      title: t("supplierproducts.table.columns.created_date"),
      key: "created_date",
      sorter: true,

      render: (rowObj: ControlcenterMelcoModelsSupplierProduct) => (
        <div> {<FormattedDate date={rowObj.created_date} />}</div>
      ),
    },
    {
      title: t("supplierproducts.table.columns.publish_date"),
      key: "publish_date",
      sorter: true,

      render: (rowObj: ControlcenterMelcoModelsSupplierProduct) => (
        <div> {<FormattedDate date={rowObj.publish_date} />}</div>
      ),
    },
    {
      title: t("supplierproducts.table.columns.publish_state"),
      key: "publish_state",
      sorter: true,
      render: (rowobj: ControlcenterMelcoModelsSupplierProduct) => {
        return <PublishedState publishedState={rowobj.publish_state ?? 1} />;
      },
    },
  ];

  const closeDrawerAction = () => {
    setIsSupplierProductEditDrawerOpenValue(false);
    setSupplierProductAddDrawerOpenFunction(false);
    refetch();
  };

  return (
    <>
      <TableActionBarRow>
        <Col>
          {
            <Search
              onSearchInputChange={(query: string) => searchQueryChange(query)}
            />
          }
        </Col>
      </TableActionBarRow>

      <Table
        showSorterTooltip={false}
        sticky
        loading={isPreviousData ? isFetching : isLoading}
        dataSource={supplierProductsData}
        columns={columns}
        onChange={onTableConfigChange}
        pagination={pagination}
        noDataMessage={t("supplierproducts.basic.no_supplier_products_message")}
        errorComponent={errorComponent}
      />

      {isSupplierProductEditDrawerOpenValue && (
        <SupplierProductEditDrawer
          isSupplierProductDrawerOpen={isSupplierProductEditDrawerOpenValue}
          supplierProductIdToUpdate={supplierProductIdToUpdate!}
          closeDrawerAction={() => {
            closeDrawerAction();
          }}
        />
      )}
      {isSupplierProductAddDrawerOpen && (
        <SupplierProductAddDrawer
          isSupplierProductDrawerOpen={isSupplierProductAddDrawerOpen}
          closeDrawerAction={() => {
            closeDrawerAction();
          }}
        />
      )}
    </>
  );
};
